import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useEffect } from 'react';
import { getAllAnalyticsPendingRequests } from 'services/pending-requests.service';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { SmallCard } from 'theme/elements/Cards';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getPendingRequestsAnalytics } from 'state/reducers/pending-requests.reducer';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { OrangeCircle } from 'theme/elements/Chips';
import { ALL_BALANCE_ADDITIONS, pendingRequestsTypes } from 'constants/requests.constants';
import { IPendingRequestAnalytics } from 'constants/pending-requests.constants';

const HIDDEN_ANALYTICS_KEYS: string[] = [
	pendingRequestsTypes.DIGITAL_BALANCES_REQUESTS,
	pendingRequestsTypes.CONTACTS,
	'undocumentedDigitalBalanceRequests',
];

// When a card should navigate to a different route than its analytics key
const NAVIGATION_OVERRIDES: Partial<Record<string, string>> = {
	[pendingRequestsTypes.BALANCES]: ALL_BALANCE_ADDITIONS,
};

export const OpenedRequests = () => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const pendingRequests = useAppSelector(getPendingRequestsAnalytics);
	const pendingRequestsPermission = hasPermission([permissionEnums.DASHBOARD_PENDING_REQUESTS_ANALYTICS]);

	useEffect(() => {
		if (pendingRequestsPermission) {
			dispatch(getAllAnalyticsPendingRequests());
		}
	}, [pendingRequestsPermission]);

	const handleCardClick = (cardName: string) => {
		navigate(`/${pagesEnum.PENDING_REQUESTS}/${cardName}`);
	};

	const cards =
		pendingRequests && Object.entries(pendingRequests).filter(([key]) => !HIDDEN_ANALYTICS_KEYS.includes(key));

	return (
		<Stack>
			<Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.common.black, marginBottom: 2 }}>
				{t('page.dashboard.pendingRequests.title')}
			</Typography>
			<Grid container spacing={4} columns={5}>
				{cards?.map(([key, data]: [string, IPendingRequestAnalytics]) => {
					const navTarget = NAVIGATION_OVERRIDES[key] ?? key;
					return (
						<Grid item xs={8} sm={'auto'} key={key} sx={{ cursor: 'pointer' }}>
							<SmallCard
								styleObj={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
								}}
								handleClick={() => handleCardClick(navTarget)}
							>
								<Stack>
									<Typography variant="boldBody3">{data.total}</Typography>
									<Stack direction="row" alignItems="center" spacing={1}>
										{data.today > 0 && <OrangeCircle />}
										<Typography
											variant="boldBody4"
											sx={{ fontWeight: data.today > 0 ? 700 : 'auto' }}
										>
											{t(`page.dashboard.pendingRequests.${key}`)}
										</Typography>
									</Stack>
								</Stack>
								<img src="/icons/arrow-left.svg" style={{ marginRight: '10px' }} />
							</SmallCard>
						</Grid>
					);
				})}
			</Grid>
		</Stack>
	);
};
